import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { WorkerChip } from './useWorkersByIds';

interface IndexWorkerRow {
  id: number;
  active: boolean;
  user?: {
    id: number;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
  avatar_url?: string | null;
}

interface IndexResponse {
  success: boolean;
  data: IndexWorkerRow[];
}

const DEBOUNCE_MS = 250;

export function useWorkerSearch(query: string) {
  const [results, setResults] = useState<WorkerChip[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    // Set synchronously — before the debounce timeout — so a consumer never
    // observes loading=false with stale/empty results in the window between
    // the query becoming active and the debounced fetch actually starting.
    setLoading(true);

    let cancelled = false;
    const handle = setTimeout(async () => {
      setError(null);
      try {
        const res = await axiosRubyClient.get<IndexResponse>(
          '/api/admin/workers',
          { params: { search: trimmed, per_page: 20 } },
        );
        if (cancelled) return;
        const rows: WorkerChip[] = (res.data.data ?? []).map((r) => ({
          id: r.id,
          active: r.active,
          avatar_url: r.avatar_url ?? null,
          user: r.user
            ? {
                id: r.user.id,
                first_name: r.user.first_name ?? null,
                last_name: r.user.last_name ?? null,
                email: r.user.email ?? null,
              }
            : null,
        }));
        setResults(rows);
      } catch (err: unknown) {
        if (cancelled) return;
        Sentry.captureException(err, {
          tags: { feature: 'comms', action: 'worker_search' },
        });
        setError((err as Error)?.message ?? 'Search failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  return { results, loading, error };
}
